import React, { useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, ValueSetterParams } from "ag-grid-community";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import SearchInput from "../../components/shared/SearchInput";
import {
  GrantIdUpdateRow,
  SelectedGrantSeed,
} from "../../types/BulkGrantUpdate";
import { useBulkUpdateGrantIds } from "../../queries/bulkGrantUpdate";
import { PreviewRow, validateRows } from "../../utils/bulkGrantIdValidation";

interface BulkUpdateGrantIdsPreviewProps {
  rows: GrantIdUpdateRow[];
  selectedGrantSeeds: SelectedGrantSeed[];
  onCancel: () => void;
}

const BulkUpdateGrantIdsPreview: React.FC<BulkUpdateGrantIdsPreviewProps> = ({
  rows,
  selectedGrantSeeds,
  onCancel,
}) => {
  const navigate = useNavigate();
  const gridApi = useRef<GridApi | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>(
    validateRows(rows)
  );

  const { mutate: updateGrantIds, isLoading: isUpdating } =
    useBulkUpdateGrantIds();

  const [errorMessage, setErrorMessage] = useState("");

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    params.api.sizeColumnsToFit();
  };

  const valueSetter = (params: ValueSetterParams): boolean => {
    const field = params.colDef.field;
    if (!field) return false;
    const updated = [...previewRows];
    const current = updated[params?.node?.rowIndex || 0];
    if (!current) return false;

    const next = {
      ...current,
      [field]: params.newValue,
    } as GrantIdUpdateRow;

    updated[params?.node?.rowIndex || 0] = next as PreviewRow;
    setPreviewRows(validateRows(updated));
    return true;
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "STATUS",
        field: "isValid",
        minWidth: 120,
        maxWidth: 140,
        pinned: "left",
        cellRenderer: (params: any) => {
          if (params.data?.isValid) {
            return (
              <div className="flex items-center gap-1 text-green-600 py-2">
                <Icon icon="mdi:check-circle" height={16} />
                <span className="text-xs">Valid</span>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 text-red-600 py-2">
              <Icon icon="mdi:alert-circle" height={16} />
              <span className="text-xs">Error</span>
            </div>
          );
        },
      },
      {
        headerName: "OLD GRANT ID",
        field: "oldGrantId",
        editable: false,
        minWidth: 180,
      },
      {
        headerName: "NEW GRANT ID",
        field: "newGrantId",
        editable: true,
        valueSetter,
        minWidth: 180,
      },
      {
        headerName: "ERRORS",
        field: "errors",
        minWidth: 260,
        flex: 1,
        autoHeight: true,
        wrapText: true,
        cellRenderer: (params: any) => {
          const errors = params.data?.errors || [];
          if (!errors.length) {
            return <span className="text-gray-400">-</span>;
          }
          return (
            <ul className="list-disc list-inside text-xs text-red-600 py-2">
              {errors.map((error: string, index: number) => (
                <li key={`${params.rowIndex}-${index}`}>{error}</li>
              ))}
            </ul>
          );
        },
      },
    ],
    [previewRows]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      menuTabs: ["filterMenuTab"] as any,
      filterParams: {
        buttons: ["reset"],
      },
      cellStyle: { paddingTop: "12px", lineHeight: "20px" },
    }),
    []
  );

  const handleSubmit = () => {
    setErrorMessage("");
    const validated = validateRows(previewRows);
    setPreviewRows(validated);

    const invalidRows = validated.filter((row) => !row.isValid);
    if (invalidRows.length > 0) {
      toast.error("Please fix row errors before submitting");
      return;
    }

    updateGrantIds(
      validated.map((row) => ({
        oldGrantId: (row.oldGrantId || "").trim(),
        newGrantId: (row.newGrantId || "").trim(),
      })),
      {
        onSuccess: (response) => {
          toast.success(
            `Updated ${
              response.updatedCount || validated.length
            } grant IDs successfully`
          );
          navigate("/options/esopOverview");
        },
        onError: (error: any) => {
          setErrorMessage(
            error?.response?.data?.errorMessage || "Failed to update grant IDs"
          );
        },
      }
    );
  };

  return (
    <VStack className="w-full h-full px-4 bg-white border-2 rounded-md min-h-[600px]">
      <HStack className="py-4 text-lg font-medium text-left bg-white border-b justify-between items-center">
        <div>
          <h6>Preview Grant ID Updates</h6>
          {selectedGrantSeeds.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Total {selectedGrantSeeds.length} selected grant(s)
            </p>
          )}
        </div>
        <HStack className="items-center gap-4 ">
          <SearchInput
            placeholder="Search"
            onChange={(e: any) => {
              if (gridApi.current) {
                gridApi.current.setQuickFilter(e.target.value);
              }
            }}
          />
        </HStack>
      </HStack>

      {errorMessage && (
        <div className="p-2 mb-4 text-red-600 bg-red-100 border border-red-400 rounded">
          <ul>
            {errorMessage.split("\n").map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="py-4 ag-theme-material w-full" style={{ height: 500 }}>
        <AgGridReact
          key="bulk-grant-id-preview-grid"
          rowData={previewRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          rowSelection="multiple"
          stopEditingWhenCellsLoseFocus
          rowClass="border-t border-dashed"
        />
      </div>

      <HStack className="justify-between gap-4 p-4 border-t w-full">
        <ButtonPrimary1
          className="text-red-500 border-red-500 hover:bg-red-50"
          onClick={onCancel}
          disabled={isUpdating}
        >
          Back
        </ButtonPrimary1>
        <ButtonPrimary onClick={handleSubmit} loading={isUpdating}>
          Submit
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
};

export default BulkUpdateGrantIdsPreview;
